import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { NumericFormat } from 'react-number-format';
import { Modal, Input, Typography, Button, DatePicker } from 'antd';
import * as yup from 'yup';
import { loadFromLocalStorageObjectFromBase64 } from '@databases/localStorage';
import { contantAuthentication } from '@contants/index';
import { apiUpdateConfigV2 } from '@services/ManageDevices/DeviceServices';
import { apiCalculateStatisticsMonth } from '@services/ManageCustomers/CustomerServices';
import { showSuccess } from '@components/notification';
import InputUploadImage from '@components/Upload/Upload';
import moment from 'moment';
import dayjs from 'dayjs';

function ModalUpdateEndMeterReading({ open, onClose, record, fetchData }) {
  const [loading, setLoading] = useState(false);
  const [endMeterReadingManual, setEndMeterReadingManual] = useState('');
  const [endMeterReadingManualReason, setEndMeterReadingManualReason] = useState('');
  const [endMeterReadingManualImage, setEndMeterReadingManualImage] = useState(null);
  const [endMeterReadingManualBy, setEndMeterReadingManualBy] = useState('');
  const [endMeterReadingManualReadAt, setEndMeterReadingManualReadAt] = useState(
    dayjs(moment().format('YYYY-MM-DD HH:mm'))
  );
  const [errors, setErrors] = useState({});

  const user = JSON.parse(loadFromLocalStorageObjectFromBase64(contantAuthentication.DATA_AUTH));

  // Synchronize values with `record` when modal opens
  useEffect(() => {
    if (open && record) {
      setEndMeterReadingManual(record?.end_meter_reading_manual ?? '');
      setEndMeterReadingManualReason(record?.end_meter_reading_manual_reason ?? '');
      setEndMeterReadingManualImage(record?.end_meter_reading_manual_image ?? null);
      setEndMeterReadingManualBy(user?.user?.user_name ?? '');
      setEndMeterReadingManualReadAt(
        record?.end_meter_reading_manual_read_at
          ? dayjs(moment(record.end_meter_reading_manual_read_at).format('YYYY-MM-DD HH:mm'))
          : dayjs(moment().format('YYYY-MM-DD HH:mm'))
      );
      setErrors({});
    }
  }, [open, record]);

  const validateInputs = () => {
    const validationSchema = yup.object().shape({
      end_meter_reading_manual: yup
        .number()
        .transform((value, originalValue) =>
          originalValue?.toString()?.trim() === '' ? null : value
        )
        .required('Vui lòng nhập chỉ số mới thủ công')
        .nullable(),
      end_meter_reading_manual_reason: yup
        .string()
        .required('Vui lòng nhập lý do')
        .max(500, 'Lý do không được vượt quá 500 ký tự')
    });

    try {
      validationSchema.validateSync(
        {
          end_meter_reading_manual: endMeterReadingManual,
          end_meter_reading_manual_reason: endMeterReadingManualReason
        },
        { abortEarly: false }
      );
      setErrors({});
      return true;
    } catch (validationErrors) {
      const errorMessages = {};
      validationErrors.inner.forEach((error) => {
        errorMessages[error.path] = error.message;
      });
      setErrors(errorMessages);
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!validateInputs()) return;

    try {
      setLoading(true);

      // Call API to update
      await apiUpdateConfigV2(
        {
          end_meter_reading_manual: Number(endMeterReadingManual),
          end_meter_reading_manual_reason: endMeterReadingManualReason,
          end_meter_reading_manual_image: endMeterReadingManualImage,
          end_meter_reading_manual_by: endMeterReadingManualBy,
          end_meter_reading_manual_at: new Date(),
          end_meter_reading_manual_read_at: endMeterReadingManualReadAt
            ? dayjs(endMeterReadingManualReadAt).format('YYYY-MM-DD HH:mm:ss')
            : undefined
        },
        record?.config_id
      );

      // Call API to recalculate statistics
      await apiCalculateStatisticsMonth({
        page: 1,
        size: 10,
        keyWord: record?.customer_id,
        community: 'HP',
        period: record?.period ? moment(record.period).format('YYYY-MM-DD') : undefined
      });

      showSuccess({ message: 'Cập nhật chỉ số mới thủ công thành công' });
      onClose();
      fetchData();
    } catch (error) {
      console.error('Validation or API error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div>
          <div>Cập nhật chỉ số mới thủ công</div>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            Khách hàng {record?.customer_name} ({record?.customer_id})
          </Typography.Text>
        </div>
      }
      visible={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={600}
      footer={[
        <Button
          key="back"
          style={{ height: '36px', backgroundColor: '#E8EBEF', color: '#192434' }}
          onClick={onClose}>
          Hủy bỏ
        </Button>,
        <Button
          key="submit"
          style={{ backgroundColor: '#0051A9', color: '#FFFF', height: '36px' }}
          loading={loading}
          onClick={handleSubmit}>
          Cập nhật
        </Button>
      ]}>
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px' }}>Chỉ số mới thủ công (m3) *</label>
        <NumericFormat
          style={{
            width: '100%',
            height: '36px',
            padding: '8px',
            borderRadius: 0,
            border: '1px solid #D9D9D9'
          }}
          placeholder="Nhập chỉ số mới thủ công"
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={3}
          value={endMeterReadingManual}
          onValueChange={(value) => setEndMeterReadingManual(value.floatValue)}
        />
        {errors.end_meter_reading_manual && (
          <Typography.Text type="danger" color="red">
            {errors.end_meter_reading_manual}
          </Typography.Text>
        )}
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px' }}>Lý do *</label>
        <Input.TextArea
          rows={3}
          placeholder="Nhập lý do cập nhật chỉ số mới thủ công"
          value={endMeterReadingManualReason}
          onChange={(e) => setEndMeterReadingManualReason(e.target.value)}
        />
        {errors.end_meter_reading_manual_reason && (
          <Typography.Text type="danger" color="red">
            {errors.end_meter_reading_manual_reason}
          </Typography.Text>
        )}
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px' }}>Ảnh chụp chỉ số</label>
        <InputUploadImage
          onChange={(value) => setEndMeterReadingManualImage(value)}
          initValue={endMeterReadingManualImage}
        />
      </div>
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px' }}>Thời gian đọc chỉ số</label>
        <DatePicker
          showTime={{ use12Hours: false }}
          format="YYYY-MM-DD HH:mm"
          style={{ width: '100%', height: '36px' }}
          value={endMeterReadingManualReadAt}
          onChange={(value) => setEndMeterReadingManualReadAt(value)}
        />
      </div>
    </Modal>
  );
}

ModalUpdateEndMeterReading.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  record: PropTypes.object,
  fetchData: PropTypes.func
};

export default ModalUpdateEndMeterReading;
